import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, useField } from 'formik';
import {
  AtsSettingsDocument,
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
  useSetAtsHiringTeamUserProvisioningEnabledMutation,
} from 'generated/graphql';
import { formatDate } from 'helpers/date';
import { useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  Button,
  EditFormWrapper,
  FormikDatepicker,
  FormikRadioButtonGroup,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { AtsSettings } from './IntegrationsTab.helpers';
import styles from './IntegrationsTab.module.scss';

const dataTestId = 'integrations-connection-form';

/**
 * Assignees only ride the assessment-order invite path; link-synced
 * candidates never get them, and each order replaces the set wholesale
 * (verified against the gateway/monolith consumers, EM-135).
 */
const ASSIGNEES_CAPTION =
  'When enabled, candidates invited through an ATS assessment order are ' +
  'automatically assigned to the ATS job’s hiring team, and each new order ' +
  're-applies those assignees — replacing any manual changes for that ' +
  'candidate. When disabled, ordered candidates arrive unassigned. Has no ' +
  'effect on candidates synced from linked roles.';

const HIRING_TEAM_PROVISIONING_CAPTION =
  'When enabled, ATS hiring team members without an SZ account are created ' +
  'as users, and users of other companies are added to this one. When ' +
  'disabled, hiring team members are only added to the role’s team if they ' +
  'are already users of this company — nobody is created and no one gains ' +
  'access to the company via the ATS. Skipped members are added ' +
  'automatically by a later sync once an admin makes them a company user.';

const ROLE_CREATION_GROUP_CAPTION =
  'Which routes the company app’s Role Wizard offers when creating a role. ' +
  'At least one must stay enabled — these only apply while an ATS is ' +
  'connected.';

const WITH_REQUISITION_CAPTION =
  'Lets users create a role by picking an ATS requisition, linking the ATS ' +
  'job at creation so its req code and candidate sync apply. When this is ' +
  'the only route enabled, “Create role” goes straight to the requisition ' +
  'picker.';

const WITHOUT_REQUISITION_CAPTION =
  'Lets users create a standalone role with no ATS requisition. Roles ' +
  'created this way don’t receive ATS candidates until a job is linked. ' +
  'When this is the only route enabled, the requisition option is hidden.';

type ConnectionCardProps = {
  company: CompanyPageCompanyFragment;
  atsSettings: AtsSettings | null;
};

export function ConnectionCard({ company, atsSettings }: ConnectionCardProps) {
  const [isEditing, setIsEditing] = useState(false);

  return isEditing ? (
    <ConnectionForm
      company={company}
      atsSettings={atsSettings}
      onCancelClick={() => setIsEditing(false)}
      onSave={() => setIsEditing(false)}
    />
  ) : (
    <ConnectionView
      company={company}
      atsSettings={atsSettings}
      onEditClick={() => setIsEditing(true)}
    />
  );
}

type ConnectionViewProps = ConnectionCardProps & {
  onEditClick: () => void;
};

function ConnectionView({
  company,
  atsSettings,
  onEditClick,
}: ConnectionViewProps) {
  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Connection</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          label="ATS"
          value={
            atsSettings
              ? `${company.atsIdentifier || 'Unknown ATS'} · Connected`
              : 'Not connected'
          }
        />
        <InfoItem
          label="ATS pull date"
          value={formatDate(
            new Date(company.atsPullJobsFromDate).toISOString(),
            'dd MMM yyyy'
          )}
        />
        <InfoItem
          label="Candidate assignees managed by integration"
          value={
            company.isAssigneeManagedViaIntegration ? 'Enabled' : 'Disabled'
          }
        />

        {atsSettings && (
          <>
            <InfoItem
              label="Create users from ATS hiring team"
              value={
                atsSettings.hiringTeamUserProvisioningEnabled
                  ? 'Enabled'
                  : 'Disabled'
              }
            />
            <h3 className={styles.groupHeading}>ATS role creation</h3>
            <InfoItem
              label="Allow role creation with a requisition"
              value={
                company.isAtsRoleCreationWithRequisitionEnabled
                  ? 'Enabled'
                  : 'Disabled'
              }
            />
            <InfoItem
              label="Allow role creation without a requisition"
              value={
                company.isAtsRoleCreationWithoutRequisitionEnabled
                  ? 'Enabled'
                  : 'Disabled'
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

type ConnectionFormValues = {
  atsPullJobsFromDate: string;
  isAssigneeManagedViaIntegration: string;
  isAtsRoleCreationWithRequisitionEnabled: string;
  isAtsRoleCreationWithoutRequisitionEnabled: string;
  isHiringTeamUserProvisioningEnabled: string;
};

type ConnectionFormProps = {
  company: CompanyPageCompanyFragment;
  atsSettings: AtsSettings | null;
  onCancelClick: () => void;
  onSave: () => void;
};

function ConnectionForm({
  company,
  atsSettings,
  onCancelClick,
  onSave,
}: ConnectionFormProps) {
  const { handleMsgType } = useNotification();
  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();
  const [setHiringTeamUserProvisioningEnabledMutation] =
    useSetAtsHiringTeamUserProvisioningEnabledMutation();

  const handleSave = async (values: ConnectionFormValues) => {
    const hiringTeamUserProvisioningEnabled =
      values.isHiringTeamUserProvisioningEnabled === EnableDisableValue.Enabled;

    try {
      await editCompanyMutation({
        variables: {
          id: company.id,
          name: company.name,
          atsPullJobsFromDate: values.atsPullJobsFromDate,
          isAssigneeManagedViaIntegration:
            values.isAssigneeManagedViaIntegration ===
            EnableDisableValue.Enabled,
          isAtsRoleCreationWithRequisitionEnabled: atsSettings
            ? values.isAtsRoleCreationWithRequisitionEnabled ===
              EnableDisableValue.Enabled
            : undefined,
          isAtsRoleCreationWithoutRequisitionEnabled: atsSettings
            ? values.isAtsRoleCreationWithoutRequisitionEnabled ===
              EnableDisableValue.Enabled
            : undefined,
        },
      });
      // The setting lives on the ATS integration, not the company, so it
      // needs its own mutation; it only runs once the company save lands.
      if (
        atsSettings &&
        hiringTeamUserProvisioningEnabled !==
          atsSettings.hiringTeamUserProvisioningEnabled
      ) {
        await setHiringTeamUserProvisioningEnabledMutation({
          variables: {
            companyId: company.id,
            enabled: hiringTeamUserProvisioningEnabled,
          },
          refetchQueries: [
            {
              query: AtsSettingsDocument,
              variables: { companyId: company.id },
            },
          ],
        });
      }
      handleMsgType({
        type: TNotification.success,
        title: 'Connection settings updated.',
        dismissable: true,
      });
      onSave();
    } catch {
      handleMsgType({
        type: TNotification.error,
        title: 'Connection settings could not be updated',
        message: 'Something went wrong. Please try again.',
        dismissable: true,
      });
    }
  };

  const initialValues: ConnectionFormValues = {
    atsPullJobsFromDate: company.atsPullJobsFromDate,
    isAssigneeManagedViaIntegration: company.isAssigneeManagedViaIntegration
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
    isAtsRoleCreationWithRequisitionEnabled:
      company.isAtsRoleCreationWithRequisitionEnabled
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
    isAtsRoleCreationWithoutRequisitionEnabled:
      company.isAtsRoleCreationWithoutRequisitionEnabled
        ? EnableDisableValue.Enabled
        : EnableDisableValue.Disabled,
    isHiringTeamUserProvisioningEnabled:
      atsSettings?.hiringTeamUserProvisioningEnabled === false
        ? EnableDisableValue.Disabled
        : EnableDisableValue.Enabled,
  };

  // Roles must be creatable somehow: blocking both routes would leave the
  // company unable to create any ATS-linked role.
  const validate = (values: ConnectionFormValues) => {
    if (
      atsSettings &&
      values.isAtsRoleCreationWithRequisitionEnabled ===
        EnableDisableValue.Disabled &&
      values.isAtsRoleCreationWithoutRequisitionEnabled ===
        EnableDisableValue.Disabled
    ) {
      const error = 'At least one role creation option must be enabled.';
      return {
        isAtsRoleCreationWithRequisitionEnabled: error,
        isAtsRoleCreationWithoutRequisitionEnabled: error,
      };
    }
    return {};
  };

  return (
    <Formik<ConnectionFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
      validate={validate}
    >
      {({ submitForm }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Connection"
          formId={dataTestId}
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id={`${dataTestId}-element`}
            className={styles.form}
          >
            <FormikDatepicker
              id="atsPullJobsFromDate"
              name="atsPullJobsFromDate"
              label="ATS pull date"
              data-testid={`${dataTestId}__ats-pull-date-datepicker`}
              useFormikField={useField}
              isClearable={false}
            />
            <FormikRadioButtonGroup
              id="isAssigneeManagedViaIntegration"
              name="isAssigneeManagedViaIntegration"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label="Candidate assignees managed by integration"
              data-testid={`${dataTestId}__assignees-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
            <span className={styles.caption}>{ASSIGNEES_CAPTION}</span>
            {atsSettings && (
              <>
                <FormikRadioButtonGroup
                  id="isHiringTeamUserProvisioningEnabled"
                  name="isHiringTeamUserProvisioningEnabled"
                  options={enableDisableRadioButtonOptions}
                  layout="horizontal"
                  label="Create users from ATS hiring team"
                  data-testid={`${dataTestId}__hiring-team-user-provisioning-radio-group`}
                  useFormikField={useField}
                  className={styles.radioGroup}
                />
                <span className={styles.caption}>
                  {HIRING_TEAM_PROVISIONING_CAPTION}
                </span>
                <h3 className={styles.groupHeading}>ATS role creation</h3>
                <span className={styles.caption}>
                  {ROLE_CREATION_GROUP_CAPTION}
                </span>
                <FormikRadioButtonGroup
                  id="isAtsRoleCreationWithRequisitionEnabled"
                  name="isAtsRoleCreationWithRequisitionEnabled"
                  options={enableDisableRadioButtonOptions}
                  layout="horizontal"
                  label="Allow role creation with a requisition"
                  data-testid={`${dataTestId}__ats-role-creation-with-requisition-radio-group`}
                  useFormikField={useField}
                  className={styles.radioGroup}
                />
                <span className={styles.caption}>
                  {WITH_REQUISITION_CAPTION}
                </span>
                <FormikRadioButtonGroup
                  id="isAtsRoleCreationWithoutRequisitionEnabled"
                  name="isAtsRoleCreationWithoutRequisitionEnabled"
                  options={enableDisableRadioButtonOptions}
                  layout="horizontal"
                  label="Allow role creation without a requisition"
                  data-testid={`${dataTestId}__ats-role-creation-without-requisition-radio-group`}
                  useFormikField={useField}
                  className={styles.radioGroup}
                />
                <span className={styles.caption}>
                  {WITHOUT_REQUISITION_CAPTION}
                </span>
              </>
            )}
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}
